/**
 * Agent memories — what an agent knows about how to act here, before it starts
 * improvising.
 *
 * The problem these solve is not that agents lack intelligence; it is that they
 * start every thread with no idea what this installation already learned. Two
 * production threads showed the cost: one reinstalled a JDK and an Android SDK
 * that had been on the machine for two weeks, and one spent an hour concluding
 * a file could not reach the user's Mac through a mechanism that already
 * existed. Neither is a reasoning failure — both agents were right given what
 * they could see. A memory is how they get to see more.
 *
 * ── Why Taskin owns this rather than the provider ─────────────────────────
 *
 * Both providers ship their own memory (Claude writes under
 * `~/.claude/projects/<repo>/memory/`, Codex keeps a SQLite store under
 * `~/.codex`), and both are already running. They are not enough for three
 * reasons, in order of severity:
 *
 *  1. **They cannot tell our users apart.** Every thread on this server runs as
 *     the same Unix user, so a provider's "personal" scope is in fact shared by
 *     everyone on the installation. What looks like a per-person layer is a
 *     global one wearing a disguise.
 *  2. **They accept whatever the agent concludes.** A wrong lesson learned once
 *     is a wrong lesson applied forever, with nobody in the loop.
 *  3. **They live on one machine's disk**, so recreating the instance loses
 *     them, and only one of the two providers scopes them per project at all.
 *
 * ── The three scopes ──────────────────────────────────────────────────────
 *
 * `general` is knowledge about this installation — the toolchains on the host,
 * the capabilities that exist, the things that are impossible here. `project`
 * is knowledge about one repository. `user` is one person's standing
 * preferences, resolved from the thread's **author** and inherited by every
 * delegate, so a thread behaves the same way from top to bottom no matter who
 * typed the last message.
 *
 * ── Index and body are different things ───────────────────────────────────
 *
 * `hook` is the one line that travels in the system prompt for every memory the
 * thread can see; `body` is what the agent reads only when the case matches.
 * That split is the whole efficiency argument, so `hook` is bounded hard and
 * `body` generously: an index that grows without limit is paid on every thread
 * forever, whereas a long body is paid only by the thread that needed it.
 *
 * Schema-only, like the rest of this package. The rules about who may approve
 * what live in the server's `AgentMemoryService`.
 */
import { Schema } from "effect";

import {
  AgentMemoryId,
  IsoDateTime,
  ManagedAgentId,
  ProjectId,
  TagId,
  ThreadId,
  TrimmedNonEmptyString,
  UserId,
} from "./baseSchemas";

/**
 * Hard bounds. These are not defensive validation — they are the design.
 *
 * `HOOK` is the expensive field: it is rendered once per memory into every
 * system prompt of every thread that can see it. At 120 characters a 64-entry
 * index costs roughly 2 KB, which is what the cap on the whole index is set
 * against.
 *
 * `BODY` is cheap by comparison and should be allowed to say everything the
 * agent needs, including commands and paths, rather than forcing an author to
 * compress a procedure into a paragraph.
 */
export const AGENT_MEMORY_SLUG_MAX_LENGTH = 64;
export const AGENT_MEMORY_TITLE_MAX_LENGTH = 120;
export const AGENT_MEMORY_HOOK_MAX_LENGTH = 120;
export const AGENT_MEMORY_TRIGGER_MAX_LENGTH = 200;
export const AGENT_MEMORY_BODY_MAX_LENGTH = 16_384;

/**
 * How many memories may reach one thread's index, and how many bytes that index
 * may occupy once rendered.
 *
 * Both exist because the failure mode is silent: nothing breaks when the index
 * reaches two hundred entries, the cost just moves into every thread on the
 * installation and nobody attributes it. When the cap bites, the resolver drops
 * the least specific scopes first (see `AGENT_MEMORY_SCOPE_PRIORITY`) and says
 * so, rather than truncating at an arbitrary point.
 */
export const AGENT_MEMORY_INDEX_MAX_ENTRIES = 64;
export const AGENT_MEMORY_INDEX_MAX_BYTES = 4_096;

/**
 * Slugs are the handle an agent sees in the index and passes to `memory_read`,
 * so they have to survive being read aloud in a prompt: lowercase, digits and
 * hyphens, nothing that needs quoting.
 */
export const AgentMemorySlug = TrimmedNonEmptyString.check(
  Schema.isMaxLength(AGENT_MEMORY_SLUG_MAX_LENGTH),
  Schema.isPattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
);
export type AgentMemorySlug = typeof AgentMemorySlug.Type;

/**
 * Where a memory applies.
 *
 * Ordered from broad to narrow. The literal order is not load-bearing anywhere
 * — priority is stated explicitly in `AGENT_MEMORY_SCOPE_PRIORITY` — but the
 * declaration reads in the order a person thinks about them.
 */
export const AgentMemoryScope = Schema.Literals(["general", "project", "user"]);
export type AgentMemoryScope = typeof AgentMemoryScope.Type;

/**
 * Which scope survives when the index cap bites, most specific first.
 *
 * A person's own standing preference and this project's procedure are worth
 * more to the turn at hand than a general fact about the installation, so
 * generality is what gets dropped. Exported rather than inlined because the
 * resolver and the settings UI must agree about it — the UI shows which
 * memories would not make the cut.
 */
export const AGENT_MEMORY_SCOPE_PRIORITY = ["user", "project", "general"] as const;

/**
 * A memory's place in its lifecycle.
 *
 * `proposed` is the whole point of the write path: an agent that learns
 * something records it here, where it is inert — it does not reach any index
 * and no agent can read it — until a person approves it. `archived` keeps a
 * retired memory readable for audit without it steering anything.
 */
export const AgentMemoryStatus = Schema.Literals(["proposed", "approved", "archived"]);
export type AgentMemoryStatus = typeof AgentMemoryStatus.Type;

export const AgentMemory = Schema.Struct({
  id: AgentMemoryId,
  scope: AgentMemoryScope,
  /** Set only for `project` scope; null otherwise. */
  projectId: Schema.NullOr(ProjectId),
  /** Set only for `user` scope; null otherwise. */
  userId: Schema.NullOr(UserId),
  slug: AgentMemorySlug,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TITLE_MAX_LENGTH)),
  /** The single line that goes in the index. Bounded hard — see the caps above. */
  hook: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_HOOK_MAX_LENGTH)),
  /**
   * When this memory applies, in the author's words.
   *
   * Separate from `hook` because they answer different questions to different
   * readers: `hook` tells the agent what the memory *is* while it scans the
   * index, `triggerHint` tells a human curator when it *fires* while they
   * decide whether to approve it. Optional — plenty of memories are obvious
   * from their hook alone.
   */
  triggerHint: Schema.NullOr(
    TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TRIGGER_MAX_LENGTH)),
  ),
  body: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_BODY_MAX_LENGTH)),
  status: AgentMemoryStatus,
  /** The thread whose agent proposed this, when one did. Null for a human-authored memory. */
  proposedByThreadId: Schema.NullOr(ThreadId),
  /** The managed agent that proposed it, when one did. */
  proposedByAgentId: Schema.NullOr(ManagedAgentId),
  approvedByUserId: Schema.NullOr(UserId),
  /**
   * How many times `memory_read` has returned this body.
   *
   * The only signal that separates a memory doing its job from one that has sat
   * in every system prompt for a month steering nothing. Without it there is no
   * honest way to decide what to archive.
   */
  readCount: Schema.Int,
  /**
   * Tags this memory carries.
   *
   * Empty means it reaches every agent and every project — the default-allow
   * rule in `@taskin-code/shared/tagMatch`. Ids rather than the whole tags: the
   * screen already holds the vocabulary, and embedding labels here would make
   * a rename look like an edit to every memory that wears it.
   */
  tagIds: Schema.Array(TagId),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AgentMemory = typeof AgentMemory.Type;

/**
 * One line of the rendered index.
 *
 * The agent never receives an `AgentMemory` — it receives these, plus whatever
 * bodies it asks for by slug. Keeping the projection explicit is what stops a
 * future field from silently ending up in every system prompt.
 */
export const AgentMemoryIndexEntry = Schema.Struct({
  slug: AgentMemorySlug,
  hook: Schema.String,
  scope: AgentMemoryScope,
});
export type AgentMemoryIndexEntry = typeof AgentMemoryIndexEntry.Type;

/**
 * What a person sends to write a memory by hand.
 *
 * `scope` decides which owner field is required, and the server is what
 * enforces that pairing — a `project` memory without a `projectId` would
 * otherwise land as a general one under a different name. Both owner fields
 * stay optional here so the same schema serves all three scopes.
 */
export const AdminMemoryCreateRequest = Schema.Struct({
  scope: AgentMemoryScope,
  projectId: Schema.optional(Schema.NullOr(ProjectId)),
  userId: Schema.optional(Schema.NullOr(UserId)),
  slug: AgentMemorySlug,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TITLE_MAX_LENGTH)),
  hook: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_HOOK_MAX_LENGTH)),
  triggerHint: Schema.optional(
    Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TRIGGER_MAX_LENGTH))),
  ),
  body: TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_BODY_MAX_LENGTH)),
  // Absent means untagged, which means available everywhere.
  tagIds: Schema.optional(Schema.Array(TagId)),
});
export type AdminMemoryCreateRequest = typeof AdminMemoryCreateRequest.Type;

/**
 * Editing a memory in place. Absent means "leave it alone".
 *
 * Neither `scope` nor `slug` is patchable, and that is deliberate: both are
 * identity. A slug is the handle agents were told to use and moving a memory
 * between scopes changes who it steers — either one is an archive plus a new
 * memory, so the audit trail shows what happened instead of a row quietly
 * becoming something else.
 */
export const AdminMemoryUpdateRequest = Schema.Struct({
  title: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TITLE_MAX_LENGTH)),
  ),
  hook: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_HOOK_MAX_LENGTH)),
  ),
  triggerHint: Schema.optional(
    Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_TRIGGER_MAX_LENGTH))),
  ),
  body: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(AGENT_MEMORY_BODY_MAX_LENGTH)),
  ),
  /**
   * A *present* list is a full replacement, not a merge — the editor always
   * sends the complete set it is showing, and a merge would make removing the
   * last tag impossible to express. Same rule as `teamIds` on an agent.
   */
  tagIds: Schema.optional(Schema.Array(TagId)),
});
export type AdminMemoryUpdateRequest = typeof AdminMemoryUpdateRequest.Type;

export const AdminMemoriesResponse = Schema.Struct({
  memories: Schema.Array(AgentMemory),
});
export type AdminMemoriesResponse = typeof AdminMemoriesResponse.Type;

/** Single-memory envelope returned by create, update and approve. */
export const AdminMemoryResponse = Schema.Struct({
  memory: AgentMemory,
});
export type AdminMemoryResponse = typeof AdminMemoryResponse.Type;

/**
 * What one thread would actually carry, so the cost of the index stops being
 * invisible.
 *
 * `bytes` and `dropped` are the two numbers a curator cannot get any other way:
 * the first is what every thread in that scope pays on every turn, the second
 * is how many memories the cap is silently keeping out of it.
 */
export const AdminMemoryIndexPreview = Schema.Struct({
  entries: Schema.Array(AgentMemoryIndexEntry),
  dropped: Schema.Int,
  rendered: Schema.NullOr(Schema.String),
  bytes: Schema.Int,
});
export type AdminMemoryIndexPreview = typeof AdminMemoryIndexPreview.Type;
